/**
 * DimensionsFields - Dimensions & Weight form fields
 *
 * Fields: length, wingspan, height, tail_height, empty_weight, max_takeoff_weight
 */

import type { UseFormReturn } from 'react-hook-form'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import type { AircraftFormValues } from '../aircraft-form-schema'

interface DimensionsFieldsProps {
  form: UseFormReturn<AircraftFormValues>
  disabled?: boolean
  /** Include weight fields (for full form only) */
  includeWeight?: boolean
  /** Include tail height (for full form only) */
  includeTailHeight?: boolean
}

export function DimensionsFields({
  form,
  disabled = false,
  includeWeight = true,
  includeTailHeight = true,
}: DimensionsFieldsProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="length"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Length (ft)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.1"
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(e.target.value ? parseFloat(e.target.value) : null)
                  }
                  disabled={disabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="wingspan"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Wingspan (ft)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.1"
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(e.target.value ? parseFloat(e.target.value) : null)
                  }
                  disabled={disabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="height"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Height (ft)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.1"
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(e.target.value ? parseFloat(e.target.value) : null)
                  }
                  disabled={disabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {includeTailHeight && (
          <FormField
            control={form.control}
            name="tail_height"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tail Height (ft)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.1"
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(e.target.value ? parseFloat(e.target.value) : null)
                    }
                    disabled={disabled}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>

      {includeWeight && (
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="empty_weight"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Empty Weight (lbs)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(e.target.value ? parseInt(e.target.value) : null)
                    }
                    disabled={disabled}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="max_takeoff_weight"
            render={({ field }) => (
              <FormItem>
                <FormLabel>MTOW (lbs)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(e.target.value ? parseInt(e.target.value) : null)
                    }
                    disabled={disabled}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
    </div>
  )
}
